// src/components/resources/calculators/CalculatorLayout.tsx

'use client'

import { logClientError } from '~/lib/logger-client'
import { useEffect, useState, type ReactNode } from 'react'
import type { ResourceTypeMap } from '~/lib/resources/types/shared/base'
import { ResourceType } from '~/lib/resources/types/shared/enums'
import { motion } from 'motion/react'
import Image from 'next/image'
import { RequestAccessLink } from '~/components/resources/RequestAccessLink'
import RiCalculatorLine from '~/icons/remix/generated/RiCalculatorLine'
import { fetchResourcesByTypeClient } from '~/lib/resources/api/client'
import { Breadcrumbs } from '~/components/resources/Breadcrumbs'
import { RelatedResourcesSection } from '~/components/resources/RelatedResourcesSection'
import useScroll from '~/lib/use-scroll'
import {
  scoreToolForCalculator,
  scoreGlossaryForCalculator,
} from '~/lib/resources/relevance/calculator-scoring'

type Calculator = Extract<ResourceTypeMap['tool'], { toolType: 'calculator' }>
type Analyzer = Extract<ResourceTypeMap['tool'], { toolType: 'analyzer' }>
type RelatedResourcesBundle = {
  calculators: Calculator[]
  analyzers: Analyzer[]
  glossaryTerms: ResourceTypeMap['glossary-term'][]
}

interface CalculatorLayoutProps {
  resource: Calculator
  children: React.ReactNode
  /**
   * Optional pre-computed related resources (calculators / analyzers /
   * glossary terms). When provided, the in-component `RelatedContent`
   * block uses these directly and skips the client-side fetch — which
   * means the related-content links render in the SSR HTML on first
   * paint instead of populating after a ~1.5–2.5s client round-trip.
   *
   * Calculator detail pages compute this server-side via
   * `computeCalculatorRelatedResources` in
   * `~/lib/resources/api/related-resources.ts` and pass it in. The
   * prop is optional so a caller that omits it still works via the
   * defensive client-fetch fallback (`RelatedContentWithFetch` below);
   * the live calculator route always pre-computes and passes it.
   */
  relatedResources?: RelatedResourcesBundle
}

/**
 * Pure renderer for a related-resources bundle. No state, no effects,
 * no fetching — just markup. Used directly when the parent pre-computes
 * the bundle server-side (the common path); the fetch fallback below
 * wraps this with a useEffect-driven fetch.
 *
 * Splitting the render from the fetch (per PR #105 review feedback)
 * means the common precomputed-prop path never creates the unused
 * AbortController / useState / useEffect machinery — the dead state
 * the previous combined component carried on every precomputed render.
 */
function RelatedContentView({
  resources,
}: {
  resources: RelatedResourcesBundle
}): ReactNode {
  return (
    <RelatedResourcesSection
      groups={[
        { title: 'Related Calculators', resources: resources.calculators },
        { title: 'Related Analyzers', resources: resources.analyzers },
        { title: 'Related Terms', resources: resources.glossaryTerms },
      ]}
    />
  )
}

/**
 * Defensive client-fetch path for a caller that doesn't pre-compute the
 * related-resources bundle. Wraps the pure view above with a
 * useEffect-driven fetch + scoring; mirrors the server-side
 * `computeCalculatorRelatedResources` selection so the two surfaces stay
 * behaviourally equivalent. The live calculator route always passes
 * server-precomputed `relatedResources`, so this path is only a fallback
 * for the optional-prop case; if that prop is ever made required, this
 * component plus the duplicated scoring functions below can be deleted,
 * leaving a single canonical scoring implementation in
 * `~/lib/resources/api/related-resources.ts`.
 */
function RelatedContentWithFetch({
  resource,
}: {
  resource: Calculator
}): ReactNode {
  const [fetchedResources, setFetchedResources] = useState<RelatedResourcesBundle>({
    calculators: [],
    analyzers: [],
    glossaryTerms: [],
  })
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false)

  useEffect(() => {
    let isMounted = true
    let abortController: AbortController | null = null

    async function fetchRelated(): Promise<void> {
      // Abort any existing fetch
      if (abortController) {
        abortController.abort()
      }
      
      // Create new abort controller for this fetch
      abortController = new AbortController()
      
      try {
        // Add a timeout to abort long-running requests
        const timeoutId = setTimeout(() => {
          if (abortController) {
            abortController.abort('Request timeout after 5s')
          }
        }, 5000)
        
        // Fetch all resources
        const [tools, glossaryTerms] = await Promise.all([
          fetchResourcesByTypeClient(ResourceType.TOOL).catch(e => {
            logClientError(e, { component: 'CalculatorLayout', context: 'Failed to fetch tools' })
            return [] // Return empty array on error
          }),
          fetchResourcesByTypeClient(ResourceType.GLOSSARY_TERM).catch(e => {
            logClientError(e, { component: 'CalculatorLayout', context: 'Failed to fetch glossary terms' })
            return [] // Return empty array on error
          })
        ])
        
        clearTimeout(timeoutId)
        
        if (!isMounted) return

        // Filter tools by type and exclude the current resource. Compare
        // BOTH `id` and `slug` — `id` and `slug` are distinct fields on a
        // resource and the old `t.id !== currentSlug` predicate could
        // include the current calculator in its own "Related Calculators"
        // list whenever `id` happened to differ from the slug. The new
        // server-side `computeCalculatorRelatedResources` uses the same
        // dual-field predicate (related-resources.ts) — keeping the two
        // surfaces behaviourally equivalent.
        const allTools = tools.filter((t): t is ResourceTypeMap['tool'] =>
          t.resourceType === ResourceType.TOOL &&
          t.id !== resource.id &&
          t.slug !== resource.slug
        )
        
        // Get calculators and analyzers
        const calculators = allTools
          .filter((t): t is Calculator => 
            'toolType' in t && t.toolType === 'calculator'
          )
          .map(calc => ({
            tool: calc,
            score: scoreToolForCalculator(calc, resource)
          }))
          .filter(({ score }) => score > 0)
          .sort((a, b) => b.score - a.score)
          .map(({ tool }) => tool)
          .slice(0, 3)

        const analyzers = allTools
          .filter((t): t is Analyzer => 
            'toolType' in t && t.toolType === 'analyzer'
          )
          .map(analyzer => ({
            tool: analyzer,
            score: scoreToolForCalculator(analyzer, resource)
          }))
          .filter(({ score }) => score > 0)
          .sort((a, b) => b.score - a.score)
          .map(({ tool }) => tool)
          .slice(0, 3)

        // Filter and score glossary terms
        const relevantTerms = glossaryTerms
          .filter((term): term is ResourceTypeMap['glossary-term'] => 
            term.resourceType === ResourceType.GLOSSARY_TERM &&
            'shortDescription' in term && 
            'definition' in term
          )
          .map(term => ({
            term,
            score: scoreGlossaryForCalculator(term as ResourceTypeMap['glossary-term'], resource)
          }))
          .filter(({ score }) => score > 0)
          .sort((a, b) => b.score - a.score)
          .map(({ term }) => term as ResourceTypeMap['glossary-term'])
          .slice(0, 3)

        if (isMounted) {
          setFetchedResources({
            calculators,
            analyzers,
            glossaryTerms: relevantTerms
          })
          setHasAttemptedFetch(true)
        }
      } catch (error: unknown) {
        if (isMounted) {
          logClientError(error, { component: 'CalculatorLayout', context: 'Error fetching related resources' })
          setHasAttemptedFetch(true)
        }
      } finally {
        if (isMounted) {
          // Remove setIsLoading
        }
      }
    }

    void fetchRelated()

    return () => {
      isMounted = false
      if (abortController) {
        abortController.abort('Component unmounted')
      }
    }
    // `resource.id` / `resource.slug` (stable scalars) — the resource
    // object reference changes on every parent re-render and would
    // re-fire the fetch unnecessarily.
  }, [resource.id, resource.slug, resource])

  // Don't flash an empty wrapper before the fetch resolves; once the
  // attempt is complete, fall back to the pure view (which handles
  // its own "all empty → null" gate).
  if (!hasAttemptedFetch) return null
  return <RelatedContentView resources={fetchedResources} />
}

/**
 * Dispatch between the precomputed (server-fetched) and fetch
 * (client-fetched) paths. Callers that pre-compute pay zero cost
 * beyond the single conditional + the pure render below; callers
 * that don't pre-compute get the legacy client fetch.
 */
function RelatedContent({
  resource,
  precomputed,
}: {
  resource: Calculator
  precomputed?: RelatedResourcesBundle
}): ReactNode {
  if (precomputed) return <RelatedContentView resources={precomputed} />
  return <RelatedContentWithFetch resource={resource} />
}

export function CalculatorLayout({
  resource,
  children,
  relatedResources,
}: CalculatorLayoutProps): JSX.Element {
  const scrolled = useScroll(50)

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      {/* Fixed top navigation bar that extends full width */}
      <div className="fixed top-0 left-0 right-0 z-40">
        {/* Background that extends up */}
        <div className={`absolute inset-0 transition-opacity duration-200 ${
          scrolled
            ? 'bg-gradient-to-b from-white via-white to-white/95 dark:from-gray-950 dark:via-gray-950 dark:to-gray-950/95'
            : 'bg-gradient-to-b from-white via-white/95 to-white/80 dark:from-gray-950 dark:via-gray-950/95 dark:to-gray-950/80'
        }`} />

        {/* Space for Navbar */}
        <div className="h-20" />

        {/* Breadcrumbs Container. The canonical title is already here via the
            `resource` prop, so the crumb label ships in the first-pass SSR
            HTML instead of arriving via a client fetch. */}
        <div className="relative pb-1">
          <Breadcrumbs leafLabel={resource.canonicalTitle ?? resource.title} />
        </div>
      </div>

      {/* Main Content - account for fixed header height */}
      <div className="pt-[148px]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
            {/* Left Column: Calculator (2/3 width) */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="min-w-0 lg:col-span-2"
            >
              <div className="rounded-2xl">
                <div className="relative h-48 w-full overflow-hidden rounded-lg">
                  <Image
                    src={resource.images?.hero?.url ?? '/images/resources/tools/adsights-digital-marketing-resource-center-interactive-tools-marketing-calculators-quizzes-analyzers-hero-image.webp'}
                    alt={resource.title}
                    fill
                    sizes="(max-width: 640px) 100vw, (max-width: 768px) 100vw, (max-width: 1024px) 66vw, (max-width: 1280px) 66vw, (max-width: 1536px) 50vw, 800px"
                    fetchPriority="high"
                    className="object-cover"
                  />
                </div>
                {children}
              </div>
            </motion.div>

            {/* Right Column: Context & Value Props (1/3 width) */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="min-w-0 lg:sticky lg:top-[148px]"
            >
              <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl mb-6">
                {resource.title}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {resource.description}
              </p>
        
              {/* Value Props */}
              <div className="mt-12 space-y-8">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-indigo-50 dark:bg-indigo-500/10">
                    <RiCalculatorLine className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white">
                      Free Calculator
                    </h3>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      No sign up required. Use this calculator as much as you need.
                    </p>
                  </div>
                </div>
              </div>

              {/* Contextual upsell into the core platform. Free calculators
                  are top-of-funnel lead magnets; this routes high-intent
                  users from a static formula to the AI analysis product
                  (both audits flagged tool pages as not funnelling into
                  the SaaS offering). */}
              <div className="mt-10 rounded-xl border border-indigo-100 bg-indigo-50 p-5 dark:border-indigo-900/40 dark:bg-indigo-900/10">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                  Stop calculating by hand
                </h3>
                <p className="mt-1.5 text-sm text-gray-600 dark:text-gray-400">
                  Want AI to track this across every creative variant — and tie it
                  back to ROAS automatically? That&rsquo;s what AdSights does.
                </p>
                <RequestAccessLink className="mt-3" />
              </div>

              {/* Related Content — server-precomputed when available
                  (calculator detail page passes `relatedResources` so
                  the links ship in SSR HTML); falls back to the
                  client-fetch path inside RelatedContent if the prop
                  is absent. */}
              <RelatedContent
                resource={resource}
                precomputed={relatedResources}
              />
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  )
} 